// ConverterFunction is shorthand for a single number in, single number out

import { AngleUnits } from "./angle";
import { ApparentPowerUnits } from "./apparentPower";
import { ConcentrationUnits } from "./concentration";
import { CurrentUnits } from "./current";
import { DensityUnits } from "./density";
import { FlowRateUnits } from "./flowRate";
import { FrequencyUnits } from "./frequency";
import { GenericUnits } from "./generic";
import { LengthUnits } from "./length";
import { LiquidRatioUnits } from "./liquidRatio";
import { MassUnits } from "./mass";
import { PercentageUnits } from "./percentage";
import { PowerUnits } from "./power";
import { PressureUnits } from "./pressure";
import { ProductivityIndexUnits } from "./productivityIndex";
import { SpeedUnits } from "./speed";
import { TemperatureUnits } from "./temperature";
import { ViscosityUnits } from "./viscosity";
import { VoltageUnits } from "./voltage";
import { VolumeUnits } from "./volume";

// to avoid null/undefined checking
type ConverterFunction = (quantity: number) => number;

/*
ConvertableUnits capture the conversions between units of the same
type/domain/family/group

- Each ConvertableUnit
  - has a string label that will be used in the UX for this unit
  - must have the ability:
    - to convert a quantity of itself into a "Standard Unit"
    - to convert a quantity the "Standard Unit" to itself.
*/
export type ConvertableUnit = {
  longLabel?: string; // for full name of unit
  shortLabel?: string; // For display when space is limited
  inSituLabel?: string; // for display in-situ, e.g., bbl/d
  label: string; // dropdown label
  toThisUnit: ConverterFunction; // given a quantity of standard unit, convert to this unit
  toStandardUnit: ConverterFunction; // given a quantity of this unit, convert to standard unit
};

export type UnitDomain = {
  options: (excludedUnits?: ConvertableUnit[] | null) => ConvertableUnit[];
};

// hold a map of all registered units for string codec
const stringsToUnits: { [key: string]: ConvertableUnit } = {};

export const registerDomain = (units: UnitDomain): void => {
  units.options().forEach(unit => {
    const key = unit.label;

    if (key in stringsToUnits) {
      // something is already in this registered spot
      throw new Error("unit registration collision!" + key);
    }

    unit.shortLabel = unit.shortLabel ?? unit.label;

    stringsToUnits[key] = unit;
  });
};

// Register all units in the app
[
  AngleUnits,
  ApparentPowerUnits,
  ConcentrationUnits,
  CurrentUnits,
  DensityUnits,
  FlowRateUnits,
  FrequencyUnits,
  GenericUnits,
  LengthUnits,
  LiquidRatioUnits,
  MassUnits,
  PercentageUnits,
  PowerUnits,
  PressureUnits,
  ProductivityIndexUnits,
  SpeedUnits,
  TemperatureUnits,
  ViscosityUnits,
  VoltageUnits,
  VolumeUnits
].map(registerDomain);

export const ToString = (unit: ConvertableUnit): string => unit.label;
export const ToUnit = (unitId: string): ConvertableUnit | null => {
  return stringsToUnits[unitId] || null;
};

export const convert = (
  value: number | null | undefined,
  fromUnit: ConvertableUnit | null | undefined,
  toUnit: ConvertableUnit | null | undefined
): number => {
  if (fromUnit && toUnit) {
    if (fromUnit === toUnit) return value || 0;
    return toUnit.toThisUnit(fromUnit.toStandardUnit(value || 0));
  }
  return value || 0;
};
